import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { UserCircle, Save, RefreshCw } from 'lucide-react';

export function RoutingPage() {
  const [agents, setAgents] = useState([]);
  const [mapping, setMapping] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [diagnose, setDiagnose] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, a, d] = await Promise.all([
        api.get('/api/routing/mapping'),
        api.get('/api/routing/agents').catch((err) => ({ agents: [], error: err.message })),
        api.get('/api/routing/diagnose').catch(() => null),
      ]);
      setMapping(m.mapping);
      setAccountId(m.account_id);
      setAgents(a.agents || []);
      setDiagnose(d);
      if (m.mapping?.agent_id) setSelectedAgent(String(m.mapping.agent_id));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!selectedAgent) return;
    setSaving(true);
    try {
      const agent = agents.find((a) => String(a.id) === selectedAgent);
      await api.post('/api/routing/mapping', {
        agent_id: parseInt(selectedAgent, 10),
        agent_email: agent?.email,
      });
      toast.success('Agent mapping saved');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove the current vendor → agent mapping?')) return;
    try {
      await api.del('/api/routing/mapping');
      toast.success('Mapping removed');
      setSelectedAgent('');
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-8" data-testid="routing-page">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Smart Handoff</h2>
        <p className="text-sm text-slate-500 mt-1">
          Pick the Chatwoot agent who should own every conversation that reaches this vendor.
          Applied automatically to all incoming flow completions.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 max-w-xl" data-testid="routing-card">
        {loading ? (
          <div className="text-sm text-slate-400 py-4">Loading…</div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-[#00D1B2]/10 flex items-center justify-center">
                <UserCircle className="w-5 h-5 text-[#00D1B2]" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Default agent</p>
                <p className="text-xs text-slate-400">Chatwoot account #{accountId ?? '—'}</p>
              </div>
            </div>

            {agents.length === 0 ? (
              <div className="text-sm text-slate-500 bg-amber-50 border border-amber-100 rounded-md p-3 space-y-2">
                <p className="font-medium text-amber-900">Smart Handoff isn't ready yet.</p>
                {diagnose?.blockers?.length > 0 ? (
                  <ul className="list-disc list-inside space-y-1 text-xs text-amber-800">
                    {diagnose.blockers.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                ) : (
                  <p className="text-xs">
                    No agents found on this Chatwoot account, or the account isn't reachable.
                    Add an agent in Chatwoot first, then click <RefreshCw className="w-3 h-3 inline mx-1" /> Refresh.
                  </p>
                )}
                {diagnose && (
                  <details className="text-[10px] text-amber-700 mt-2">
                    <summary className="cursor-pointer font-medium">Diagnostics</summary>
                    <pre className="mt-1 whitespace-pre-wrap break-words">
                      {JSON.stringify({
                        chatwoot_url_set: diagnose.chatwoot_url_set,
                        platform_token_set: diagnose.platform_token_set,
                        chatwoot_user_id: diagnose.chatwoot_user_id,
                        chatwoot_account_id: diagnose.chatwoot_account_id,
                        agent_token_obtained: diagnose.agent_token_obtained,
                        agents_visible: diagnose.agents_visible,
                      }, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            ) : (
              <>
                <Label className="text-[10px] uppercase tracking-wide text-slate-400">Assign to</Label>
                <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                  <SelectTrigger data-testid="routing-agent-select">
                    <SelectValue placeholder="Pick a Chatwoot agent" />
                  </SelectTrigger>
                  <SelectContent>
                    {agents.map((a) => (
                      <SelectItem key={a.id} value={String(a.id)}>
                        {a.name || a.email} {a.role ? `· ${a.role}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}

            <div className="flex items-center gap-2 mt-5">
              <Button
                data-testid="routing-save-btn"
                onClick={save}
                disabled={saving || !selectedAgent}
                className="bg-[#00D1B2] hover:bg-[#00B89C] text-white gap-2"
              >
                <Save className="w-4 h-4" />
                {mapping ? 'Update mapping' : 'Save mapping'}
              </Button>
              <Button variant="outline" onClick={load} className="gap-2">
                <RefreshCw className="w-4 h-4" /> Refresh
              </Button>
              {mapping && (
                <Button
                  variant="ghost"
                  onClick={remove}
                  data-testid="routing-delete-btn"
                  className="text-rose-600 ml-auto"
                >
                  Remove
                </Button>
              )}
            </div>

            {mapping && (
              <div className="mt-5 text-xs text-slate-500 bg-slate-50 rounded-md p-3" data-testid="routing-current-mapping">
                Currently routed to agent <span className="font-mono">#{mapping.agent_id}</span>
                {mapping.agent_email ? ` (${mapping.agent_email})` : ''}
                <br />
                Updated: {new Date(mapping.updated_at).toLocaleString()}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
